import { useTranslation } from "react-i18next";
import { ArrowRight, Code2, Database, LogIn, Palette, Shapes, UserPlus } from "lucide-react";
import { STYLE_TEMPLATES, type StyleTemplate } from "../../config/style-templates";
import { ICON_SET_PREVIEW, ICON_SETS } from "../../config/icon-sets";
import { COLOR_PALETTES, paletteSwatch } from "../../libs/color-palettes";
import { REGISTER_URL, isAuthConfigured, login as authLogin } from "../../libs/auth";
import { ECOSYSTEM_LINKS } from "../../config/ecosystem-links";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SetIcon, useIconSet } from "./icon-set-source";

/** Satellite-ish stripes for the Hybrid template (no palette of its own). */
const HYBRID_SWATCH = ["#3d4a2f", "#5b6b3f", "#8a8f6a", "#c9b27c", "#2f4f6f"];
const SHOWCASE = ["classic-modern", "night-modern", "hybrid-modern", "voyager-flat"];

/** Home page for visitors who aren't signed in: what the editor does + sign in / sign up. */
export function SignedOutLanding({ onOpenEditor }: { onOpenEditor(): void }) {
  const { t } = useTranslation();
  const canLogin = isAuthConfigured();
  const templates = SHOWCASE.map((id) => STYLE_TEMPLATES.find((x) => x.id === id)).filter((x): x is StyleTemplate => !!x);

  const features = [
    { icon: Palette, title: t("Mẫu & bảng màu"), desc: t("8 mẫu, 9 bảng màu, đổi màu từng lớp.") },
    { icon: Shapes, title: t("Bộ icon & chữ địa điểm"), desc: t("4 bộ icon, font chữ, cỡ, vị trí, viền chữ.") },
    { icon: Database, title: t("Lớp dữ liệu của bạn"), desc: t("Thêm hàng trăm nghìn điểm, tuyến, vùng tuỳ chỉnh của bạn từ Local Store lên bản đồ mà không lo giật lag.") },
    { icon: Code2, title: t("Chia sẻ & nhúng"), desc: t("Style URL, iframe, code cho web và mobile.") },
  ];
  const steps = [t("Đăng nhập"), t("Chọn mẫu & chỉnh"), t("Lưu & nhúng")];

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-wd-key="signed-out-landing">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-[#d6e2f1] bg-gradient-to-br from-[#f3f7fc] via-white to-[#e8f0fa] text-[#122c4f] dark:border-white/10 dark:from-[#0d2240] dark:via-[#0f2747] dark:to-[#132f55] dark:text-white">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(640px_360px_at_88%_8%,rgba(41,104,209,0.14),transparent_60%)] dark:bg-[radial-gradient(700px_380px_at_85%_10%,rgba(56,120,200,0.45),transparent_60%),radial-gradient(500px_300px_at_0%_100%,rgba(16,185,129,0.12),transparent_60%)]"
        />
        <div className="relative grid gap-8 p-6 sm:p-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div className="space-y-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#122c4f]/15 bg-white px-3 py-1 text-xs font-medium text-[#122c4f]/80 dark:border-white/20 dark:bg-white/10 dark:text-white/90">
              <span className="size-1.5 rounded-full bg-emerald-400" />
              Vietmap Map Editor
            </span>
            <h1 className="text-3xl leading-tight font-extrabold tracking-tight sm:text-4xl">
              {t("Tạo và tuỳ chỉnh style bản đồ mang dấu ấn thương hiệu của bạn")}
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-slate-600 dark:text-white/75">
              {t("Đổi màu nền bản đồ, bộ icon, chữ trên nền bản đồ Vietmap; thêm tệp dữ liệu của bạn trước khi nhúng vào web/app.")}
            </p>
            {canLogin ? (
              <div className="flex flex-wrap items-center gap-3">
                <Button size="lg" className="dark:bg-white dark:text-[#122c4f] dark:hover:bg-white/90" onClick={() => authLogin()} data-wd-key="landing:login">
                  <LogIn />
                  {t("Đăng nhập")}
                </Button>
                <Button size="lg" variant="outline" className="bg-white dark:border-white/30 dark:bg-transparent dark:text-white dark:hover:bg-white/10 dark:hover:text-white" asChild>
                  <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" data-wd-key="register-link">
                    <UserPlus />
                    {t("Đăng ký miễn phí")}
                  </a>
                </Button>
              </div>
            ) : (
              <p className="text-sm text-slate-600 dark:text-white/70">{t("Hệ thống đăng nhập chưa được cấu hình. Liên hệ admin để được hỗ trợ.")}</p>
            )}
            <p className="text-xs text-slate-500 dark:text-white/55">
              {t("Dùng tài khoản Vietmap Console")}
            </p>
          </div>

          <div className="hidden grid-cols-2 gap-3 sm:grid" aria-hidden="true">
            {templates.map((tpl, i) => <TemplateTile key={tpl.id} template={tpl} className={cn(i % 2 === 1 && "translate-y-4")} />)}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {features.map(({ icon: Icon, title, desc }) => (
          <Card key={title} className="gap-0 py-0 shadow-none">
            <CardContent className="space-y-2 p-5">
              <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-blue-400/15 dark:text-blue-300">
                <Icon className="size-5" />
              </div>
              <h3 className="font-semibold">{title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* How it works */}
      <section className="flex flex-col gap-4 rounded-xl border bg-muted/40 p-5 md:flex-row md:items-center dark:bg-muted/20">
        <ol className="flex flex-1 flex-col gap-3 md:flex-row md:items-center md:gap-3">
          {steps.map((s, i) => (
            <li key={s} className="flex items-center gap-2 text-sm">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground dark:bg-blue-500 dark:text-white">{i + 1}</span>
              <span className="font-medium">{s}</span>
              {i < steps.length - 1 && <ArrowRight className="ms-1 hidden size-4 text-muted-foreground md:block" />}
            </li>
          ))}
        </ol>
        <Button variant="outline" onClick={onOpenEditor} className="shrink-0">
          {t("Mở editor")}
          <ArrowRight />
        </Button>
      </section>

      {/* The rest of the Vietmap ecosystem (same links as index.html's intro). */}
      <footer className="flex flex-wrap gap-x-12 gap-y-6 border-t pt-6 pb-2" data-wd-key="landing-footer">
        {ECOSYSTEM_LINKS.map((group) => (
          <section key={group.title}>
            <h2 className="mb-2 text-sm font-semibold">{t(group.title)}</h2>
            <ul className="space-y-1">
              {group.links.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="text-sm text-muted-foreground hover:text-foreground hover:underline">
                    {t(link.label)}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </footer>
    </div>
  );
}

/** A template drawn without a map: palette stripes + its icon set. */
function TemplateTile({ template, className }: { template: StyleTemplate; className?: string }) {
  const { t } = useTranslation();
  const palette = COLOR_PALETTES.find((p) => p.id === template.paletteId);
  const swatch = palette ? paletteSwatch(palette) : HYBRID_SWATCH;
  const set = ICON_SETS.find((s) => s.id === template.iconSetId) ?? null;
  const icons = useIconSet(set?.kind === "svg" ? set : null);
  return (
    <div className={cn("overflow-hidden rounded-xl border border-[#d6e2f1] bg-white shadow-lg shadow-[#122c4f]/10 dark:border-white/15 dark:bg-white/10 dark:shadow-black/20 dark:backdrop-blur-sm", className)}>
      <div className="relative h-24">
        <div className="absolute inset-0 flex">
          {swatch.map((c, i) => <span key={i} className="flex-1" style={{ background: c }} />)}
        </div>
        {/* A road and a river across the stripes, so it reads as a map. */}
        <span className="absolute inset-x-0 top-[58%] h-1.5 -rotate-6 bg-white/70" />
        <span className="absolute -inset-x-2 top-[28%] h-2 rotate-3 rounded-full" style={{ background: swatch[swatch.length - 1], filter: "brightness(1.15)" }} />
        {icons.status === "ready" && (
          <div className="absolute start-2 bottom-2 flex gap-1 rounded-md bg-white/90 px-1.5 py-1 dark:bg-slate-900/85">
            {ICON_SET_PREVIEW.map((n) => <SetIcon key={n} loaded={icons.loaded} name={n} size={16} />)}
          </div>
        )}
      </div>
      <div className="px-3 py-2 text-sm font-semibold text-[#122c4f] dark:text-white">{t(template.name)}</div>
    </div>
  );
}
